export type PropertyStatus = 'Selling' | 'Sold Out' | 'Sale Closed'

export type PropertyCategory = 'house' | 'land'

export type Property = {
  id: string
  slug: string
  category: PropertyCategory
  name: string
  type?: string // For houses: Apartment, Bungalow, Duplex, etc.
  location: string
  city: string
  status: PropertyStatus
  // Pricing — Zylus does not publish prices publicly; these are indicative starting prices
  // based on Lagos/Abuja/Ogun market ranges for the area. Confirm with sales.
  priceFrom?: string
  plotSizes?: string[] // For land
  beds?: number
  baths?: number
  sqft?: string
  tagline: string
  intro: string
  features: string[]
  highlights: { label: string; value: string }[]
  gallery: string[]
  videoId?: string // YouTube video ID
  brochureUrl?: string // PDF link (placeholder until real one available)
  flyerImage?: string // Promotional flyer
  hero: string
}

const placeholderPdf = '#' // Replace with real brochure URLs when available

// =========================
// HOUSES
// =========================
export const houses: Property[] = [
  {
    id: 'h1',
    slug: 'zylus-residence',
    category: 'house',
    name: 'Zylus Residence',
    type: 'Apartment',
    location: 'Hutu, Abuja',
    city: 'Abuja',
    status: 'Sale Closed',
    priceFrom: 'From ₦55M',
    beds: 3,
    baths: 3,
    sqft: '1,800',
    tagline:
      'Contemporary apartment living in the heart of Abuja, where serenity meets sophistication.',
    intro:
      'Zylus Residence is a contemporary apartment development designed for the discerning Nigerian who values quality, proximity to the capital, and a refined lifestyle. Each unit is built with premium finishes and thoughtful layouts that maximise light, space, and comfort.',
    features: [
      '24/7 Security & CCTV',
      'Backup Power',
      'Treated Water Supply',
      'Paved Internal Roads',
      'Communal Green Spaces',
      "Children's Play Area",
      'Premium Finishes',
      'Smart Door Locks',
    ],
    highlights: [
      { label: 'Bedrooms', value: '3' },
      { label: 'Bathrooms', value: '3' },
      { label: 'Built-up', value: '1,800 sqft' },
      { label: 'Title', value: 'C of O' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=85',
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=85',
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=85',
      'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200&q=85',
    ],
    videoId: 'kRJBlu-BIEQ',
    brochureUrl: placeholderPdf,
    flyerImage:
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=85',
    hero: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1600&q=85',
  },
  {
    id: 'h2',
    slug: 'bolton-height',
    category: 'house',
    name: 'Bolton Height',
    type: 'Apartment',
    location: 'Lekki Phase 1, Lagos',
    city: 'Lagos',
    status: 'Selling',
    priceFrom: '₦270M',
    beds: 4,
    baths: 4,
    sqft: '2,800',
    tagline: 'Address of the city — Lekki Phase 1 living redefined.',
    intro:
      "Bolton Height is a flagship apartment block on the Lekki Phase 1 axis — the most coveted postcode in Lagos. Floor-to-ceiling glazing, double-height lobbies, and resort-grade amenities deliver the kind of city living usually reserved for the world's great capitals.",
    features: [
      'Electric Car Charging Stations',
      'Automated Home Features',
      'Swimming Pool',
      'World Class Recreational Area',
      'Gym, Lounge and more!',
      '24/7 Security & Power',
    ],
    highlights: [
      { label: 'Bedrooms', value: '4' },
      { label: 'Bathrooms', value: '4' },
      { label: 'Built-up', value: '2,800 sqft' },
      { label: 'Title', value: "Governor's Consent" },
    ],
    gallery: [
      'https://cdn.magicpatterns.com/uploads/emnrNKwaeXZJNCgZmEUfwr/DJI_0185.jpg',
      'https://cdn.magicpatterns.com/uploads/caFeBdzQ6APpYtxAEqWZPN/DJI_0186.jpg',
      'https://cdn.magicpatterns.com/uploads/smRiCYuDGoD98WoKCz5C5K/DJI_0202.jpg',
      'https://cdn.magicpatterns.com/uploads/kdXd928zYK6d9fpLKZmSHH/DJI_0209.jpg',
      'https://cdn.magicpatterns.com/uploads/5b9MUJrrBihXLxp8FhTAaz/DSC08049.jpg',
    ],
    videoId:
      'https://cdn.magicpatterns.com/uploads/hC6bia4KQQUAppTZM6Jkpp/WhatsApp Video 2026-05-12 at 10.01.54 AM.mp4',
    brochureUrl: placeholderPdf,
    flyerImage:
      'https://cdn.magicpatterns.com/uploads/qHi3vbWt1TWCNqLCRyhTPJ/WhatsApp_Image_2026-05-12_at_10.00.45_AM.jpg',
    hero: 'https://cdn.magicpatterns.com/uploads/emnrNKwaeXZJNCgZmEUfwr/DJI_0185.jpg',
  },
  {
    id: 'h3',
    slug: 'lekki-avana',
    category: 'house',
    name: 'Lekki Avana',
    type: 'Bungalow',
    location: 'Idera Scheme, Eleko, Epe, Lagos',
    city: 'Lagos',
    status: 'Sale Closed',
    priceFrom: 'From ₦42M',
    beds: 3,
    baths: 3,
    sqft: '1,600',
    tagline: 'Coastal calm meets contemporary craft.',
    intro:
      'Lekki Avana is a bungalow community tucked inside the Idera Scheme in Eleko — one of the fastest-appreciating corridors in Lagos. Designed for first-time owners and smart investors, the homes are built with timeless silhouettes and modern interiors.',
    features: [
      'Gated Estate',
      '24/7 Security',
      'Paved Roads & Drainage',
      'Streetlights',
      'Recreational Areas',
      'Boreholes & Treated Water',
      "Boys' Quarters Option",
      'Solar-ready Wiring',
    ],
    highlights: [
      { label: 'Bedrooms', value: '3' },
      { label: 'Bathrooms', value: '3' },
      { label: 'Built-up', value: '1,600 sqft' },
      { label: 'Title', value: 'Excision in Progress' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200&q=85',
      'https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?w=1200&q=85',
      'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=1200&q=85',
    ],
    videoId: 'cATYjVawmZ8',
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1600&q=85',
  },
  {
    id: 'h4',
    slug: 'lekki-avana-phase-2',
    category: 'house',
    name: 'Lekki Avana Phase II',
    type: 'Bungalow',
    location: 'Eleran Igbe Bus Stop, Lagos',
    city: 'Lagos',
    status: 'Sale Closed',
    priceFrom: 'From ₦48M',
    beds: 3,
    baths: 3,
    sqft: '1,700',
    tagline:
      'Phase Two of the most loved bungalow estate on the Lekki corridor.',
    intro:
      'Building on the success of Phase One, Lekki Avana Phase II expands the bungalow vision with larger plots, deeper landscaping, and a new generation of smart-ready homes — all within an established, fully-served community.',
    features: [
      'Gated Community',
      'CCTV Surveillance',
      'Underground Drainage',
      'Family Park',
      'Treated Water Supply',
      'Standby Power',
      'Premium Floor Tiles',
      'Designer Kitchens',
    ],
    highlights: [
      { label: 'Bedrooms', value: '3' },
      { label: 'Bathrooms', value: '3' },
      { label: 'Built-up', value: '1,700 sqft' },
      { label: 'Title', value: 'C of O' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1200&q=85',
      'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1200&q=85',
      'https://images.unsplash.com/photo-1600563438938-a9a27216b4f5?w=1200&q=85',
    ],
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1600&q=85',
  },
  {
    id: 'h5',
    slug: 'zylus-court-extension',
    category: 'house',
    name: 'Zylus Court Extension',
    type: 'Apartment',
    location: 'Bogije, Ibeju-Lekki, Lagos',
    city: 'Lagos',
    status: 'Selling',
    priceFrom: 'From ₦95M',
    beds: 4,
    baths: 4,
    sqft: '2,400',
    tagline:
      'The next chapter of the iconic Zylus Court — bigger, bolder, smarter.',
    intro:
      "Zylus Court Extension is the natural evolution of Zylus' signature Bogije estate. Set on the Lekki–Epe Expressway, the homes blend contemporary architecture with clever space planning, delivering luxury that is genuinely livable.",
    features: [
      '24/7 Manned Security',
      'CCTV & Access Control',
      'Smart Home Pre-wiring',
      'Backup Power Infrastructure',
      'Underground Drainage',
      'Streetlights & Paved Roads',
      'Swimming Pool & Clubhouse',
      "Children's Park",
    ],
    highlights: [
      { label: 'Bedrooms', value: '4' },
      { label: 'Bathrooms', value: '4' },
      { label: 'Built-up', value: '2,400 sqft' },
      { label: 'Title', value: 'C of O' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200&q=85',
      'https://images.unsplash.com/photo-1600210492493-0946911123ea?w=1200&q=85',
      'https://images.unsplash.com/photo-1600585154363-67eb9e2e2099?w=1200&q=85',
      'https://images.unsplash.com/photo-1600573472592-401b489a3cdc?w=1200&q=85',
    ],
    videoId: 'kRJBlu-BIEQ',
    brochureUrl: placeholderPdf,
    flyerImage:
      'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200&q=85',
    hero: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1600&q=85',
  },
  {
    id: 'h6',
    slug: 'lekki-pride-1',
    category: 'house',
    name: 'Lekki Pride 1',
    type: 'Apartment',
    location: 'Abraham Adesanya, Ogombo Road, Lekki',
    city: 'Lagos',
    status: 'Sold Out',
    priceFrom: 'Sold Out',
    beds: 3,
    baths: 3,
    sqft: '1,900',
    tagline: 'The original Pride — the estate that started it all.',
    intro:
      'Lekki Pride 1 is the foundational estate of the celebrated Pride series. Located off Abraham Adesanya, it set the standard for community-led, value-rich estate living that Zylus has since perfected across Phase 2, 3 and beyond.',
    features: [
      'Gated Community',
      '24/7 Security',
      'Drainage Network',
      'Recreational Park',
      'Boreholes',
      'Standby Power',
      'Premium Tiling',
      'Modern Kitchens',
    ],
    highlights: [
      { label: 'Bedrooms', value: '3' },
      { label: 'Bathrooms', value: '3' },
      { label: 'Built-up', value: '1,900 sqft' },
      { label: 'Title', value: 'C of O' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=85',
      'https://images.unsplash.com/photo-1600585152915-d208bec867a1?w=1200&q=85',
    ],
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=85',
  },
  {
    id: 'h7',
    slug: 'lekki-pride-2',
    category: 'house',
    name: 'Apartments at Lekki Pride 2',
    type: 'Apartment',
    location: 'Abraham Adesanya, Ogombo Road, Lekki, Lagos',
    city: 'Lagos',
    status: 'Sold Out',
    priceFrom: 'Sold Out',
    beds: 3,
    baths: 3,
    sqft: '2,000',
    tagline: 'Phase Two of the Pride series — sold out at record speed.',
    intro:
      'Lekki Pride 2 expanded the Pride lifestyle with larger floorplates and upgraded common areas. Demand was so high the estate sold out within months — a testament to the Zylus delivery promise.',
    features: [
      'Gated Estate',
      '24/7 Security',
      'CCTV',
      'Underground Drainage',
      "Children's Park",
      'Treated Water',
      'Standby Power',
      'Modern Finishes',
    ],
    highlights: [
      { label: 'Bedrooms', value: '3' },
      { label: 'Bathrooms', value: '3' },
      { label: 'Built-up', value: '2,000 sqft' },
      { label: 'Title', value: 'C of O' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1600566753051-6057da53d68f?w=1200&q=85',
      'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1200&q=85',
    ],
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1600566753051-6057da53d68f?w=1600&q=85',
  },
]

// =========================
// LAND
// =========================
export const lands: Property[] = [
  {
    id: 'l1',
    slug: 'zylus-olumo-pride',
    name: 'Zylus Olumo Pride',
    category: 'land',
    location: 'Abeokuta, Ogun State',
    city: 'Abeokuta',
    status: 'Selling',
    priceFrom: 'From ₦8M / plot',
    plotSizes: ['1 Acre', '550 sqm'],
    tagline: 'Heritage meets opportunity — own a piece of Abeokuta.',
    intro:
      "Zylus Olumo Pride is a master-planned land estate in Abeokuta, Ogun State — the gateway to the South-West. With Lagos' rapid spillover and the Lagos-Ibadan corridor opening up, this is one of the smartest land plays available today.",
    features: [
      'Gated Perimeter',
      'Survey Plan',
      'Layout Approval in Progress',
      'Tarred Internal Roads',
      'Drainage Network',
      'Streetlights',
      'Strategic Location',
      'Flexible Payment Plans',
    ],
    highlights: [
      { label: 'Sizes', value: '1 Acre · 550 sqm' },
      { label: 'Title', value: 'Excision in Progress' },
      { label: 'Payment', value: '6–24 months' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&q=85',
      'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1200&q=85',
    ],
    videoId: 'qnRxRuxRW3M',
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1600&q=85',
  },
  {
    id: 'l2',
    slug: 'zylus-chrystland-city',
    name: 'Zylus Chrystland City',
    category: 'land',
    location: 'St Augustine University, Ilara-Epe, Epe, Lagos',
    city: 'Lagos',
    status: 'Selling',
    priceFrom: 'From ₦5.5M / 300sqm',
    plotSizes: ['500 sqm', '300 sqm'],
    tagline: 'Adjacent to St Augustine University — the new academic city.',
    intro:
      'Zylus Chrystland City is positioned right next to St Augustine University in Ilara-Epe — a corridor experiencing the strongest land appreciation in Lagos State. Strategic for both end-users and serious investors.',
    features: [
      'Estate Perimeter Fence',
      'Strategic University Adjacency',
      'Survey & Layout Plan',
      'Drainage System',
      'Tarred Access Road',
      'Streetlights',
      'Recreational Reserve',
      'Flexible Payment',
    ],
    highlights: [
      { label: 'Sizes', value: '500 · 300 sqm' },
      { label: 'Title', value: 'C of O' },
      { label: 'Payment', value: '6–18 months' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1200&q=85',
      'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&q=85',
    ],
    videoId: 'eR63qOMrE58',
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1600&q=85',
  },
  {
    id: 'l3',
    slug: 'ade-prime-estate',
    name: 'Ade Prime Estate',
    category: 'land',
    location: 'Lagelu, Iwo Road, Ibadan',
    city: 'Ibadan',
    status: 'Selling',
    priceFrom: 'From ₦4.5M / 300sqm',
    plotSizes: ['500 sqm', '300 sqm'],
    tagline: 'Prime Ibadan land at an entry-level investor price point.',
    intro:
      'Ade Prime Estate sits in Lagelu, on the bustling Iwo Road axis of Ibadan. With the Lagos-Ibadan rail and expressway expansion driving migration, this is land that earns its name.',
    features: [
      'Perimeter Fencing',
      'Survey Plan',
      'Approved Layout',
      'Internal Roads',
      'Drainage',
      'Estate Streetlights',
      'Strategic Iwo Road Adjacency',
      'Flexible Payment Plans',
    ],
    highlights: [
      { label: 'Sizes', value: '500 · 300 sqm' },
      { label: 'Title', value: 'Government Allocation' },
      { label: 'Payment', value: '6–24 months' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1200&q=85',
    ],
    videoId: 'xWDbdxTAzsY',
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600&q=85',
  },
  {
    id: 'l4',
    slug: 'tiwa-garden-city-phase-2',
    name: 'Tiwa Garden City Phase II',
    category: 'land',
    location: 'Eleran Igbe Bus Stop, Ibeju-Lekki, Lagos',
    city: 'Lagos',
    status: 'Selling',
    priceFrom: 'From ₦18M / 500sqm',
    plotSizes: ['500 sqm'],
    tagline: 'The legendary Tiwa, expanded.',
    intro:
      'Tiwa Garden City Phase II builds on the wild success of Phase I. Set on the Lekki-Epe corridor at Eleran Igbe, it remains one of the most sought-after land investments in Lagos.',
    features: [
      'Perimeter Fence',
      '24/7 Security',
      'Tarred Roads',
      'Drainage Network',
      'Streetlights',
      'Recreational Centre',
      'Commercial Reserve',
      'Family Parks',
    ],
    highlights: [
      { label: 'Sizes', value: '500 sqm' },
      { label: 'Title', value: 'C of O' },
      { label: 'Payment', value: '6–24 months' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=85',
      'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1200&q=85',
    ],
    videoId: 'GIWxjBs58uA',
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1600&q=85',
  },
  {
    id: 'l5',
    slug: 'zylus-prime-city',
    name: 'Zylus Prime City',
    category: 'land',
    location: 'Eleran Igbe Bus Stop, Ibeju-Lekki, Lagos',
    city: 'Lagos',
    status: 'Sold Out',
    priceFrom: 'Sold Out',
    plotSizes: ['600 sqm', '300 sqm'],
    tagline: 'Sold out by overwhelming demand.',
    intro:
      'Zylus Prime City was a flagship offering on the Lekki-Epe axis. Demand exceeded supply within the first allocation window — a testament to the strength of the Zylus brand and the value of this corridor.',
    features: [
      'Premium Location',
      'Estate Perimeter',
      'Tarred Internal Roads',
      'Drainage Network',
      'Streetlights',
      'Family Park',
    ],
    highlights: [
      { label: 'Sizes', value: '600 · 300 sqm' },
      { label: 'Title', value: 'C of O' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&q=85',
    ],
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1600&q=85',
  },
  {
    id: 'l6',
    slug: 'arizon-estate',
    name: 'Arizon Estate',
    category: 'land',
    location: 'Eleran Igbe Bus Stop, Ibeju-Lekki, Lagos',
    city: 'Lagos',
    status: 'Selling',
    priceFrom: 'From ₦6M / 300sqm',
    plotSizes: ['500 sqm', '300 sqm'],
    tagline: 'Smart-priced land in the heart of Ibeju-Lekki.',
    intro:
      "Arizon Estate brings smart-pricing to one of Lagos' most strategic corridors. Whether you're land-banking or building, this is a no-regret entry point.",
    features: [
      'Estate Perimeter',
      'Survey Plan',
      'Layout Approval',
      'Drainage',
      'Estate Roads',
      'Streetlights',
      'Recreational Reserve',
      'Flexible Payment',
    ],
    highlights: [
      { label: 'Sizes', value: '500 · 300 sqm' },
      { label: 'Title', value: 'Excision in Progress' },
      { label: 'Payment', value: '6–24 months' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1200&q=85',
    ],
    videoId: '3RuYVrEBdAU',
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1600&q=85',
  },
  {
    id: 'l7',
    slug: 'treasure-park-estate',
    name: 'Treasure Park Estate',
    category: 'land',
    location: 'Akanu Ibiam Airport, Caritas University, NNPC Depot, Enugu',
    city: 'Enugu',
    status: 'Selling',
    priceFrom: 'From ₦5M / 300sqm',
    plotSizes: ['500 sqm', '300 sqm'],
    tagline: "Enugu's next great address.",
    intro:
      'Treasure Park Estate is positioned in the most strategic node of Enugu — between the Akanu Ibiam International Airport, Caritas University, and the NNPC Depot. Demand drivers in every direction.',
    features: [
      'Airport Adjacency',
      'University Proximity',
      'Estate Fence',
      'Drainage Network',
      'Estate Roads',
      'Streetlights',
      'Recreational Park',
      'Flexible Payment Plans',
    ],
    highlights: [
      { label: 'Sizes', value: '500 · 300 sqm' },
      { label: 'Title', value: 'Government Allocation' },
      { label: 'Payment', value: '6–24 months' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1200&q=85',
    ],
    videoId: 'UwiD9rkcYuc',
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600&q=85',
  },
  {
    id: 'l8',
    slug: 'zylus-court-phase-2',
    name: 'Zylus Court Phase II',
    category: 'land',
    location: 'Opp. Beechwood Estate, Bogije, Lagos',
    city: 'Lagos',
    status: 'Sold Out',
    priceFrom: 'Sold Out',
    plotSizes: ['500 sqm'],
    tagline: 'The land phase of the iconic Zylus Court.',
    intro:
      'Zylus Court Phase II offered serviced plots opposite Beechwood Estate in Bogije — one of the strongest land-value pockets in Lagos. Fully subscribed.',
    features: [
      'Estate Perimeter',
      'Tarred Roads',
      'Drainage',
      'Streetlights',
      'Family Park',
      '24/7 Security',
    ],
    highlights: [
      { label: 'Sizes', value: '500 sqm' },
      { label: 'Title', value: 'C of O' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&q=85',
    ],
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1600&q=85',
  },
  {
    id: 'l9',
    slug: 'signum-estate',
    name: 'Signum Estate',
    category: 'land',
    location: 'Oreki Community, Lagos State',
    city: 'Lagos',
    status: 'Sold Out',
    priceFrom: 'Sold Out',
    plotSizes: ['500 sqm'],
    tagline: 'A signature estate in the Oreki community.',
    intro:
      'Signum Estate was a boutique land offering in the Oreki community — fully subscribed within weeks of launch.',
    features: [
      'Estate Perimeter',
      'Drainage',
      'Internal Roads',
      'Streetlights',
      'Family Park',
    ],
    highlights: [
      { label: 'Sizes', value: '500 sqm' },
      { label: 'Title', value: 'Excision in Progress' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1200&q=85',
    ],
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1600&q=85',
  },
  {
    id: 'l10',
    slug: 'eti-ile-phase-2',
    name: 'Eti Ile Phase 2',
    category: 'land',
    location: 'Lafenwa, Abeokuta',
    city: 'Abeokuta',
    status: 'Sold Out',
    priceFrom: 'Sold Out',
    plotSizes: ['600 sqm', '500 sqm', '300 sqm'],
    tagline: 'The Abeokuta classic — fully subscribed.',
    intro:
      'Eti Ile Phase 2 offered Abeokuta land in the strategic Lafenwa node. Fully subscribed and under build-out.',
    features: [
      'Perimeter Fence',
      'Drainage Network',
      'Estate Roads',
      'Streetlights',
      'Recreational Park',
    ],
    highlights: [
      { label: 'Sizes', value: '600 · 500 · 300 sqm' },
      { label: 'Title', value: 'C of O' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&q=85',
    ],
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1600&q=85',
  },
  {
    id: 'l11',
    slug: 'lekki-pride-3',
    name: 'Lekki Pride 3',
    category: 'land',
    location: 'Ogombo Road, Abraham Adesanya',
    city: 'Lagos',
    status: 'Sold Out',
    priceFrom: 'Sold Out',
    plotSizes: ['500 sqm', '300 sqm'],
    tagline: 'The third Pride — sold out within the launch window.',
    intro:
      'Lekki Pride 3 sustained the Pride momentum with serviced land plots off Ogombo Road, Abraham Adesanya. Demand again exceeded supply.',
    features: [
      'Estate Perimeter',
      'Tarred Roads',
      'Drainage',
      'Streetlights',
      '24/7 Security',
    ],
    highlights: [
      { label: 'Sizes', value: '500 · 300 sqm' },
      { label: 'Title', value: 'C of O' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1200&q=85',
    ],
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1600&q=85',
  },
  {
    id: 'l12',
    slug: 'hilton-city',
    name: 'Hilton City',
    category: 'land',
    location: 'Kurudu, Abuja',
    city: 'Abuja',
    status: 'Sold Out',
    priceFrom: 'Sold Out',
    plotSizes: ['600 sqm', '500 sqm', '300 sqm'],
    tagline: 'A capital-city classic.',
    intro:
      'Hilton City was a flagship Abuja land offering in the Kurudu node. Subscribed in record time.',
    features: [
      'Capital City Adjacency',
      'Estate Perimeter',
      'Drainage Network',
      'Estate Roads',
      'Streetlights',
    ],
    highlights: [
      { label: 'Sizes', value: '600 · 500 · 300 sqm' },
      { label: 'Title', value: 'Government Allocation' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1200&q=85',
    ],
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600&q=85',
  },
  {
    id: 'l13',
    slug: 'kapital-city-estate',
    name: 'Kapital City Estate',
    category: 'land',
    location: 'Epe, Lagos',
    city: 'Lagos',
    status: 'Sold Out',
    priceFrom: 'Sold Out',
    plotSizes: ['500 sqm', '300 sqm'],
    tagline: 'Kapital, by name and by nature.',
    intro:
      "Kapital City Estate offered serviced plots in Epe — Lagos' fastest-growing investment frontier. Fully sold.",
    features: [
      'Estate Perimeter',
      'Drainage',
      'Estate Roads',
      'Streetlights',
      'Family Park',
    ],
    highlights: [
      { label: 'Sizes', value: '500 · 300 sqm' },
      { label: 'Title', value: 'C of O' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&q=85',
    ],
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1600&q=85',
  },
  {
    id: 'l14',
    slug: 'tiwa-commercial-hub',
    name: 'Tiwa Commercial Hub',
    category: 'land',
    location: 'Aiyetoro, Lekki-Epe Expressway, Lagos',
    city: 'Lagos',
    status: 'Sold Out',
    priceFrom: 'Sold Out',
    plotSizes: ['1,000 sqm', '300 sqm'],
    tagline: 'Where commerce meets the corridor.',
    intro:
      'Tiwa Commercial Hub offered prime commercial land on the Lekki-Epe Expressway — a once-in-a-generation opportunity now fully subscribed.',
    features: [
      'Frontage on Expressway',
      'Mixed-use Zoning',
      'Estate Perimeter',
      'Drainage Network',
      'Estate Roads',
      'Streetlights',
    ],
    highlights: [
      { label: 'Sizes', value: '1,000 · 300 sqm' },
      { label: 'Use', value: 'Mixed Use' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1200&q=85',
    ],
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1600&q=85',
  },
  {
    id: 'l15',
    slug: 'tiwa-garden-city',
    name: 'Tiwa Garden City',
    category: 'land',
    location: 'Aiyetoro, Lekki-Epe Expressway, Lagos',
    city: 'Lagos',
    status: 'Sold Out',
    priceFrom: 'Sold Out',
    plotSizes: ['500 sqm', '250 sqm'],
    tagline: 'The estate that built the Tiwa legend.',
    intro:
      'The original Tiwa Garden City — the estate that defined a generation of Zylus investors. Phase I is fully sold; the legacy continues with Phase II.',
    features: [
      'Estate Perimeter',
      'Tarred Roads',
      'Drainage',
      'Streetlights',
      'Family Park',
      'Commercial Reserve',
    ],
    highlights: [
      { label: 'Sizes', value: '500 · 250 sqm' },
      { label: 'Title', value: 'C of O' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&q=85',
    ],
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1600&q=85',
  },
  {
    id: 'l16',
    slug: 'western-hilltop-estate',
    name: 'Western Hilltop Estate',
    category: 'land',
    location: 'Ikola Alagbado, Ogun State',
    city: 'Ogun',
    status: 'Sold Out',
    priceFrom: 'Sold Out',
    plotSizes: ['500 sqm', '300 sqm'],
    tagline: 'A peaceful hilltop close to Lagos.',
    intro:
      'Western Hilltop Estate offered elevated land in Ikola Alagbado — close enough to Lagos to commute, far enough to breathe. Fully sold.',
    features: [
      'Hilltop Location',
      'Estate Perimeter',
      'Drainage',
      'Estate Roads',
      'Streetlights',
    ],
    highlights: [
      { label: 'Sizes', value: '500 · 300 sqm' },
      { label: 'Title', value: 'Excision in Progress' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1200&q=85',
    ],
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=1600&q=85',
  },
]

export const allProperties: Property[] = [...houses, ...lands]

export function getPropertyBySlug(slug: string): Property | undefined {
  return allProperties.find((p) => p.slug === slug)
}

export function getRelated(p: Property, count = 3): Property[] {
  const pool = (p.category === 'house' ? houses : lands).filter(
    (x) => x.slug !== p.slug,
  )
  // Prefer same status & city
  return [...pool]
    .sort((a, b) => {
      const aScore =
        (a.status === p.status ? 2 : 0) + (a.city === p.city ? 1 : 0)
      const bScore =
        (b.status === p.status ? 2 : 0) + (b.city === p.city ? 1 : 0)
      return bScore - aScore
    })
    .slice(0, count)
}
